import type { OrderException, OrderLineInput, OrderStatus, StaffOrderView } from "@kitchen/schemas";
import { Alert, Button, Chip, faDigits, formatDate, formatMoney, formatTime, Panel, Seg } from "@kitchen/ui";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { api, type TreeNode } from "../../api.js";
import {
  addDays,
  EXCEPTION_LABEL,
  lineKey,
  PHASE_LABEL,
  STATUS_LABEL,
  STATUS_TONE,
  tehranToday,
  toLineInputs,
} from "../../labels.js";
import { ErrorLine, useAction } from "../common.js";

interface ReviewRow {
  id: string;
  version: number;
  orderingUnitId: string;
  status: OrderStatus;
  portions: number;
  exceptions: OrderException[];
  reviewingBy: { personId: string; since: string }[];
}
interface Review {
  orders: ReviewRow[];
  notSubmitted: string[];
}
interface Named {
  id: string;
  name: string;
  active?: boolean;
}

/** Units by id with "organization — unit" names, from the organization tree. */
function useUnitNames() {
  const tree = useQuery({ queryKey: ["organizations"], queryFn: () => api<TreeNode[]>("/organizations") });
  return useMemo(() => {
    const names = new Map<string, string>();
    const walk = (nodes: TreeNode[]) => {
      for (const n of nodes) {
        // Names are data and may carry Latin digits; the screen always shows Persian digits.
        for (const u of n.units)
          names.set(u.id, faDigits(u.name === n.name ? n.name : `${n.name} — ${u.name}`));
        walk(n.children);
      }
    };
    walk(tree.data ?? []);
    return names;
  }, [tree.data]);
}

/**
 * The manager's day (F3-T13, design v2 Orders.html + ReviewQueue.html): every order of a day and meal with its
 * exceptions first, the units that have not ordered, and the decisions — approve, return, adjust, cancel,
 * change requests. Bulk approval is kept out of the way and needs the count typed (DL-012).
 */
export function OrdersPage() {
  const [date, setDate] = useState(tehranToday);
  const meals = useQuery({ queryKey: ["meals"], queryFn: () => api<Named[]>("/menu/lists/meals") });
  const activeMeals = (meals.data ?? []).filter((m) => m.active !== false);
  const [mealId, setMealId] = useState<string | null>(null);
  const meal = mealId ?? activeMeals[0]?.id ?? null;
  const [selected, setSelected] = useState<string | null>(null);
  const units = useUnitNames();

  const review = useQuery({
    queryKey: ["orders-review", date, meal],
    queryFn: () => api<Review>(`/orders/review?date=${date}&mealId=${meal ?? ""}`),
    enabled: meal !== null,
    refetchInterval: 30_000,
  });
  const rows = useMemo(() => {
    const list = [...(review.data?.orders ?? [])];
    // Orders that need a look first, then waiting, then the rest (req §6.6).
    const rank = (r: ReviewRow) =>
      r.exceptions.length ? 0 : r.status === "submitted" ? 1 : r.status === "cancelled" ? 3 : 2;
    return list.sort(
      (a, b) =>
        rank(a) - rank(b) ||
        (units.get(a.orderingUnitId) ?? "").localeCompare(units.get(b.orderingUnitId) ?? "", "fa"),
    );
  }, [review.data, units]);

  const waiting = rows.filter((r) => r.status === "submitted");
  const clean = waiting.filter((r) => r.exceptions.length === 0);
  const live = rows.filter((r) => r.status !== "cancelled");

  const bulk = useAction(async () => {
    const typed = window.prompt(
      `برای تأیید گروهی ${faDigits(clean.length)} سفارش بدون استثنا، تعداد را بنویسید:`,
    );
    if (!typed) return;
    await api("/orders/bulk-approve", {
      body: {
        orders: clean.map((r) => ({ id: r.id, version: r.version })),
        typedCount: Number(typed.replace(/[۰-۹]/g, (d) => String("۰۱۲۳۴۵۶۷۸۹".indexOf(d)))),
      },
    });
  }, [["orders-review"]]);

  return (
    <div className="stack">
      <div className="row wrap" style={{ gap: "var(--s3)", alignItems: "center" }}>
        <Button
          variant="ghost"
          icon="right"
          onClick={() => {
            setDate(addDays(date, -1));
            setSelected(null);
          }}
        >
          روز قبل
        </Button>
        <div className="t-title" aria-live="polite">
          {formatDate(date)}
        </div>
        <Button
          variant="ghost"
          icon="left"
          onClick={() => {
            setDate(addDays(date, 1));
            setSelected(null);
          }}
        >
          روز بعد
        </Button>
        {date !== tehranToday() && (
          <Button
            variant="ghost"
            onClick={() => {
              setDate(tehranToday());
              setSelected(null);
            }}
          >
            امروز
          </Button>
        )}
        <span className="grow" />
        {activeMeals.length > 1 && meal && (
          <Seg
            label="وعده"
            value={meal}
            onChange={(v) => {
              setMealId(v);
              setSelected(null);
            }}
            options={activeMeals.map((m) => ({ value: m.id, label: m.name }))}
          />
        )}
      </div>

      <div className="grid" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))" }}>
        <Kpi label="سفارش" value={live.length} />
        <Kpi label="پرس" value={live.reduce((n, r) => n + r.portions, 0)} />
        <Kpi label="منتظر تأیید" value={waiting.length} />
        <Kpi label="نیاز به بررسی" value={rows.filter((r) => r.exceptions.length).length} hot />
        <Kpi label="ثبت‌نشده" value={review.data?.notSubmitted.length ?? 0} hot />
      </div>

      <div
        className="grid"
        style={{ gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1.1fr)", alignItems: "start" }}
      >
        <Panel
          title="سفارش‌ها"
          actions={
            clean.length > 1 ? (
              <Button
                variant="ghost"
                icon="checks"
                disabled={bulk.busy}
                onClick={() => {
                  bulk.run(undefined);
                }}
              >
                تأیید گروهی…
              </Button>
            ) : undefined
          }
        >
          {review.isLoading && <p className="muted">در حال بارگیری…</p>}
          {!review.isLoading && rows.length === 0 && (
            <p className="muted">برای این روز و وعده سفارشی نیست.</p>
          )}
          <div className="items">
            {rows.map((r) => (
              <button
                key={r.id}
                type="button"
                className={`item${r.id === selected ? " is-active" : ""}${r.status === "cancelled" ? " dim" : ""}`}
                aria-current={r.id === selected ? "true" : undefined}
                onClick={() => {
                  setSelected(r.id);
                }}
                style={{
                  width: "100%",
                  textAlign: "start",
                  background: r.id === selected ? "var(--card)" : undefined,
                }}
              >
                <div className="grow">
                  <div className="m">{units.get(r.orderingUnitId) ?? "واحد"}</div>
                  <div className="row wrap" style={{ gap: "var(--s1)" }}>
                    <Chip tone={STATUS_TONE[r.status]}>{STATUS_LABEL[r.status]}</Chip>
                    {r.exceptions.map((e) => (
                      <Chip key={e} tone={e === "change-request" ? "coral" : "warn"} plain>
                        {EXCEPTION_LABEL[e]}
                      </Chip>
                    ))}
                    {r.reviewingBy.length > 0 && <Chip plain>در حال بررسی</Chip>}
                  </div>
                </div>
                <span className="num b">{faDigits(r.portions)}</span>
              </button>
            ))}
          </div>
          <ErrorLine error={bulk.error} />
          {(review.data?.notSubmitted.length ?? 0) > 0 && (
            <div className="stack" style={{ marginTop: "var(--s4)" }}>
              <h3 className="t-lead">هنوز ثبت نکرده‌اند</h3>
              <div className="row wrap" style={{ gap: "var(--s2)" }}>
                {review.data?.notSubmitted.map((u) => (
                  <Chip key={u} tone="coral" plain>
                    {units.get(u) ?? "واحد"}
                  </Chip>
                ))}
              </div>
              <p className="t-cap muted">پیگیری تلفنی؛ از «ثبت تلفنی» می‌توانید به‌جای آن‌ها ثبت کنید.</p>
            </div>
          )}
        </Panel>

        {selected ? (
          <OrderDetail
            key={selected}
            id={selected}
            unitName={units.get(rows.find((r) => r.id === selected)?.orderingUnitId ?? "") ?? ""}
          />
        ) : (
          <Panel>
            <p className="muted">یک سفارش را انتخاب کنید.</p>
          </Panel>
        )}
      </div>
    </div>
  );
}

function Kpi({ label, value, hot }: { label: string; value: number; hot?: boolean }) {
  return (
    <div className="card kpi" style={hot && value > 0 ? { color: "var(--coral-ink)" } : undefined}>
      <small>{label}</small>
      <b className="num">{faDigits(value)}</b>
    </div>
  );
}

function OrderDetail({ id, unitName }: { id: string; unitName: string }) {
  const order = useQuery({ queryKey: ["order", id], queryFn: () => api<StaffOrderView>(`/orders/${id}`) });
  const items = useQuery({ queryKey: ["menu-items"], queryFn: () => api<Named[]>("/menu/items") });
  const tiers = useQuery({ queryKey: ["tiers"], queryFn: () => api<Named[]>("/menu/lists/tiers") });
  const name = (itemId: string) => items.data?.find((i) => i.id === itemId)?.name ?? "—";
  const tierName = (tierId: string | null) =>
    tierId ? (tiers.data?.find((t) => t.id === tierId)?.name ?? "") : "";
  const [edit, setEdit] = useState<Record<string, number> | null>(null);

  // "Being reviewed by …" for the other managers (req §6.6): a heartbeat while this order is open.
  useEffect(() => {
    const beat = () => void api(`/orders/${id}/reviewing`, { body: {} }).catch(() => undefined);
    beat();
    const t = setInterval(beat, 30_000);
    return () => {
      clearInterval(t);
    };
  }, [id]);

  const refresh = [["order", id], ["orders-review"]];
  const o = order.data;
  const approve = useAction(async () => {
    if (o) await api(`/orders/${id}/approve`, { body: { version: o.version } });
  }, refresh);
  const giveBack = useAction(async () => {
    if (!o) return;
    const note = window.prompt("یادداشت برای رابط (مثلاً «گوشت نیست، جایگزین شود»):");
    if (note?.trim()) await api(`/orders/${id}/return`, { body: { version: o.version, note: note.trim() } });
  }, refresh);
  const cancel = useAction(async () => {
    if (!o) return;
    const reason = window.prompt("دلیل لغو؟ (به همه‌ی بخش‌های درگیر خبر داده می‌شود)");
    if (reason?.trim())
      await api(`/orders/${id}/cancel`, { body: { version: o.version, reason: reason.trim() } });
  }, refresh);
  const save = useAction(async () => {
    if (!o || !edit) return;
    const needsReason = !["draft", "submitted", "returned"].includes(o.status);
    const reason = needsReason ? window.prompt("دلیل تغییر؟ (در ردپا می‌ماند)") : "";
    if (needsReason && !reason?.trim()) return;
    const lines: OrderLineInput[] = toLineInputs(o.lines)
      .map((l) => ({ ...l, quantity: edit[lineKey(l)] ?? l.quantity }))
      .filter((l) => l.quantity > 0);
    await api(`/orders/${id}/adjust`, { body: { version: o.version, lines, reason: reason?.trim() ?? "" } });
    setEdit(null);
  }, refresh);
  const decide = useAction(
    async (arg: {
      crId: string;
      crVersion: number;
      decision: "accept" | "accept-with-note" | "counter" | "reject";
    }) => {
      const cr = o?.changeRequests.find((c) => c.id === arg.crId);
      if (!cr) return;
      let body: Record<string, unknown> = { decision: arg.decision, version: arg.crVersion, note: "" };
      if (arg.decision !== "accept") {
        const note = window.prompt(
          arg.decision === "reject" ? "پیام محترمانه به رابط (دلیل):" : "یادداشت برای رابط:",
        );
        if (!note?.trim()) return;
        body = { ...body, note: note.trim() };
      }
      if (arg.decision === "counter") {
        const lines = cr.lines.map((l) => {
          const typed = window.prompt(
            `پیشنهاد شما برای «${name(l.menuItemId)} ${tierName(l.tierId)}» (درخواست: ${faDigits(l.quantity)})`,
            String(l.quantity),
          );
          return { ...l, quantity: Number(typed ?? l.quantity) || l.quantity };
        });
        body = { ...body, lines };
      }
      await api(`/change-requests/${arg.crId}/decide`, { body });
    },
    refresh,
  );

  if (!o)
    return (
      <Panel>
        <p className="muted">در حال بارگیری…</p>
      </Panel>
    );
  const pending = o.changeRequests.filter((c) => c.status === "pending");
  const editable = !["cancelled", "delivered", "delivered-short"].includes(o.status);
  const total = o.lines.reduce((s, l) => s + (l.price.unitAmount ?? 0) * l.orderedQuantity, 0);
  const error = approve.error ?? giveBack.error ?? cancel.error ?? save.error ?? decide.error;

  return (
    <Panel title={unitName} actions={<Chip tone={STATUS_TONE[o.status]}>{STATUS_LABEL[o.status]}</Chip>}>
      <div className="stack">
        <p className="t-cap muted">
          {PHASE_LABEL[o.phase]}
          {o.submittedAt && ` · ثبت ${formatTime(o.submittedAt)}`}
          {o.flags.enteredByStaff && " · ثبت توسط کارکنان"}
          {o.flags.late && " · دیرهنگام"}
        </p>
        {o.reviewingBy.length > 1 && (
          <Alert level="normal" icon="eye" title="مدیر دیگری هم همین سفارش را باز کرده است" />
        )}

        {pending.map((cr) => (
          <div key={cr.id} className="card stack" style={{ borderInlineStart: "4px solid var(--coral)" }}>
            <div className="b" style={{ color: "var(--coral-ink)" }}>
              درخواست تغییر بعد از قفل پخت
            </div>
            <p className="t-cap muted">سفارش اصلی تا تصمیم شما معتبر است و آشپزخانه همان را می‌بیند.</p>
            <table className="tbl">
              <thead>
                <tr>
                  <th>غذا</th>
                  <th className="num">الان</th>
                  <th className="num">درخواست</th>
                </tr>
              </thead>
              <tbody>
                {cr.lines.map((l) => {
                  const now = o.lines.find((x) => lineKey(x) === lineKey(l))?.orderedQuantity ?? 0;
                  return (
                    <tr key={lineKey(l)}>
                      <td>
                        {name(l.menuItemId)} <span className="muted">{tierName(l.tierId)}</span>
                      </td>
                      <td className="num">{faDigits(now)}</td>
                      <td
                        className="num b"
                        style={l.quantity !== now ? { color: "var(--coral-ink)" } : undefined}
                      >
                        {faDigits(l.quantity)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {cr.note && <p className="ink2">«{cr.note}»</p>}
            <div className="row wrap" style={{ gap: "var(--s2)" }}>
              <Button
                variant="primary"
                onClick={() => {
                  decide.run({ crId: cr.id, crVersion: cr.version, decision: "accept" });
                }}
              >
                پذیرش
              </Button>
              <Button
                onClick={() => {
                  decide.run({ crId: cr.id, crVersion: cr.version, decision: "accept-with-note" });
                }}
              >
                پذیرش با یادداشت
              </Button>
              <Button
                onClick={() => {
                  decide.run({ crId: cr.id, crVersion: cr.version, decision: "counter" });
                }}
              >
                پیشنهاد عدد دیگر
              </Button>
              <Button
                variant="ghost"
                onClick={() => {
                  decide.run({ crId: cr.id, crVersion: cr.version, decision: "reject" });
                }}
              >
                عذرخواهی و رد
              </Button>
            </div>
          </div>
        ))}

        <div className="tbl-wrap">
          <table className="tbl">
            <thead>
              <tr>
                <th>غذا</th>
                <th>سطح</th>
                <th className="num">تعداد</th>
                <th className="num">فی</th>
              </tr>
            </thead>
            <tbody>
              {o.lines.map((l) => {
                const k = lineKey(l);
                return (
                  <tr key={l.id}>
                    <td>
                      {name(l.menuItemId)}
                      {l.extra && <Chip plain>اضافه</Chip>}
                      {l.note && <div className="t-cap muted">{l.note}</div>}
                    </td>
                    <td>{tierName(l.tierId)}</td>
                    <td className="num">
                      {edit ? (
                        <input
                          className="input num"
                          style={{ width: 88 }}
                          inputMode="numeric"
                          aria-label={`تعداد ${name(l.menuItemId)} ${tierName(l.tierId)}`}
                          value={faDigits(edit[k] ?? l.orderedQuantity)}
                          onChange={(e) => {
                            const n = Number(
                              e.target.value
                                .replace(/[۰-۹]/g, (d) => String("۰۱۲۳۴۵۶۷۸۹".indexOf(d)))
                                .replace(/\D/g, ""),
                            );
                            setEdit({ ...edit, [k]: n });
                          }}
                        />
                      ) : (
                        faDigits(l.orderedQuantity)
                      )}
                    </td>
                    <td className="num">
                      {l.price.unitAmount === null ? (
                        <Chip tone="warn" plain>
                          بی‌قیمت
                        </Chip>
                      ) : (
                        formatMoney(l.price.unitAmount)
                      )}
                      {l.price.state === "manual" && <div className="t-cap muted">دستی</div>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="row-between">
          <span className="muted">جمع</span>
          <span className="b num">{formatMoney(total)}</span>
        </div>
        {o.contactNote && <p className="ink2">یادداشت رابط: «{o.contactNote}»</p>}
        {o.kitchenNote && <p className="ink2">یادداشت برای سرآشپز: «{o.kitchenNote}»</p>}
        {o.returnNote && <p className="ink2">برگشت داده شد: «{o.returnNote}»</p>}

        <div className="row wrap" style={{ gap: "var(--s2)", justifyContent: "flex-end" }}>
          {editable && !edit && (
            <Button
              variant="ghost"
              icon="edit"
              onClick={() => {
                setEdit({});
              }}
            >
              ویرایش تعداد
            </Button>
          )}
          {edit && (
            <>
              <Button
                variant="ghost"
                onClick={() => {
                  setEdit(null);
                }}
              >
                انصراف
              </Button>
              <Button
                variant="primary"
                icon="check"
                disabled={save.busy}
                onClick={() => {
                  save.run(undefined);
                }}
              >
                ذخیره‌ی تغییرات
              </Button>
            </>
          )}
          {!edit && o.status === "submitted" && (
            <>
              <Button
                variant="ghost"
                icon="back"
                disabled={giveBack.busy}
                onClick={() => {
                  giveBack.run(undefined);
                }}
              >
                برگشت با یادداشت
              </Button>
              <Button
                variant="primary"
                size="lg"
                icon="check"
                disabled={approve.busy}
                onClick={() => {
                  approve.run(undefined);
                }}
              >
                تأیید سفارش
              </Button>
            </>
          )}
          {!edit && ["submitted", "returned", "approved", "in-production"].includes(o.status) && (
            <Button
              variant="ghost"
              icon="x"
              disabled={cancel.busy}
              onClick={() => {
                cancel.run(undefined);
              }}
            >
              لغو
            </Button>
          )}
        </div>
        <ErrorLine error={error} />
      </div>
    </Panel>
  );
}
